import React, { useEffect, useState } from 'react';
import FitTable from '../components/FitTable';
import { useAuth0 } from '@auth0/auth0-react';

interface Player {
  player_id: number;
  first_name: string;
  last_name: string;
  handedness: string;
  team: string;
  handicap: number;
  tournament_handicap: number;
  league_status: string;
  // joined from user_profiles + email_aliases
  user_id: string | null;
  username: string | null;
  account_email: string | null;
  aliases: string | null; // comma-separated from STRING_AGG
  has_rounds?: boolean;    // true when the player has any recorded score
}

// One labelled field inside a row's expandable detail panel. An optional hint
// renders an info icon that toggles the hint text on tap/click (so it works on
// touch devices, where native `title` hover tooltips don't) — the `title` stays
// for desktop hover as a bonus.
const Detail: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => {
  const [showHint, setShowHint] = useState(false);
  return (
    <div>
      <div className="bga-detail-label">
        {label}
        {hint && (
          <button
            type="button"
            title={hint}
            aria-label={showHint ? 'Hide info' : 'Show info'}
            aria-expanded={showHint}
            onClick={() => setShowHint((v) => !v)}
            style={{
              marginLeft: 5, background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', color: showHint ? 'var(--bga-green, #1a7d4b)' : '#94a3b8',
              fontSize: 13, lineHeight: 1,
            }}
          >ⓘ</button>
        )}
      </div>
      {hint && showHint && (
        <div style={{
          fontSize: 11.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0,
          color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0',
          borderRadius: 6, padding: '6px 8px', margin: '4px 0 2px', maxWidth: 280,
        }}>
          {hint}
        </div>
      )}
      <div style={{ fontSize: 14 }}>{children}</div>
    </div>
  );
};

const HCP_OVERRIDE_HINT =
  'Manual override — only applies when automatic handicap calculation is turned off ' +
  '(a platform-wide setting, which a season can override). When auto handicap is on, ' +
  'a rolling index from recent rounds is used instead.';

const Players: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editPlayerId, setEditPlayerId] = useState<number | null>(null);
  const [editedPlayer, setEditedPlayer] = useState<Partial<Player>>({});
  const [sortConfig, setSortConfig] = useState<{ key: keyof Player; direction: 'asc' | 'desc' } | null>(null);
  const [newPlayer, setNewPlayer] = useState({ first_name: '', last_name: '', email: '' });
  const [adding, setAdding] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState('');
  const APIURL = process.env.REACT_APP_API_URL;

  const toggleExpand = (playerId: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId); else next.add(playerId);
      return next;
    });

  async function authHeader() {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeader();
        const res = await fetch(`${APIURL}/players`, { headers });
        if (!res.ok) throw new Error();
        setData(await res.json());
      } catch { setError('Error fetching data'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key: keyof Player) => {
    const direction: 'asc' | 'desc' =
      sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setData((prev) =>
      [...prev].sort((a, b) => {
        const av = a[key] ?? ''; const bv = b[key] ?? '';
        if (av < bv) return direction === 'asc' ? -1 : 1;
        if (av > bv) return direction === 'asc' ? 1 : -1;
        return 0;
      })
    );
  };

  const handleEditClick = (p: Player) => { setEditPlayerId(p.player_id); setEditedPlayer({ ...p }); };
  const handleCancel = () => { setEditPlayerId(null); setEditedPlayer({}); };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if ((name === 'handicap' || name === 'tournament_handicap') && !/^-?\d*\.?\d*$/.test(value)) return;
    setEditedPlayer((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (playerId: number) => {
    try {
      const headers = await authHeader();
      const updated = { ...editedPlayer, handicap: editedPlayer.handicap || 0, tournament_handicap: editedPlayer.tournament_handicap || 0 };
      const res = await fetch(`${APIURL}/players/${playerId}`, { method: 'PUT', headers, body: JSON.stringify(updated) });
      if (!res.ok) throw new Error();
      setData((prev) => prev.map((p) => (p.player_id === playerId ? { ...p, ...updated } : p)));
      setEditPlayerId(null);
    } catch { setError('Error saving player data.'); }
  };

  const handleAddPlayer = async () => {
    if (!newPlayer.first_name || !newPlayer.last_name) { alert('First Name and Last Name are required.'); return; }
    if (!newPlayer.email) { alert('Email is required — it is used to create the player\'s app account.'); return; }
    setAdding(true);
    try {
      const headers = await authHeader();
      const res = await fetch(`${APIURL}/players`, { method: 'POST', headers, body: JSON.stringify(newPlayer) });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to add player.');
      const added = await res.json();
      setData((prev) => [...prev, added]);
      setNewPlayer({ first_name: '', last_name: '', email: '' });
      setShowAddModal(false);
      if (added.invitation_sent) {
        alert(`Player created! An invitation email has been sent to ${newPlayer.email}.`);
      } else if (added.auth0_created) {
        alert('Player created, but the invitation email could not be sent. Ask the player to use "Forgot Password".');
      } else {
        alert('Player record created, but the account could not be set up. Check Auth0 configuration.');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  // Deactivate a player who has rounds: keeps all history, but removes them from
  // the round-entry selection list and revokes app access. Reversible via restore.
  const handleDeactivate = async (player: Player) => {
    if (!window.confirm(`Deactivate ${player.first_name} ${player.last_name}? They'll be hidden from new round entry and lose app access. Their scores and history are kept, and you can reactivate them later.`)) return;
    try {
      const headers = await authHeader();
      const res = await fetch(`${APIURL}/players/${player.player_id}/revoke`, { method: 'POST', headers });
      if (!res.ok) throw new Error();
      setData((prev) => prev.map((p) => p.player_id === player.player_id ? { ...p, league_status: 'Removed' } : p));
    } catch { setError('Failed to deactivate player.'); }
  };

  const handleRestore = async (player: Player) => {
    try {
      const headers = await authHeader();
      const res = await fetch(`${APIURL}/players/${player.player_id}/restore`, { method: 'POST', headers });
      if (!res.ok) throw new Error();
      setData((prev) => prev.map((p) => p.player_id === player.player_id ? { ...p, league_status: 'Member' } : p));
    } catch { setError('Failed to restore access.'); }
  };

  // (Re)send an invitation for a player whose account setup never completed
  // (no Auth0 user / no username link). Creates/links the account and emails the
  // password-set invite, associating the player properly.
  const handleInvite = async (player: Player) => {
    const email = window.prompt(
      `Send an account invitation for ${player.first_name} ${player.last_name}.\nEnter the email address to invite:`,
      player.account_email || ''
    );
    if (!email) return;
    try {
      const headers = await authHeader();
      const res = await fetch(`${APIURL}/players/${player.player_id}/invite`, {
        method: 'POST', headers, body: JSON.stringify({ email }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to send invitation.');
      setData((prev) => prev.map((p) => p.player_id === player.player_id
        ? { ...p, user_id: body.user_id, username: body.username, account_email: body.account_email }
        : p));
      alert(body.invitation_sent
        ? `Invitation sent to ${email}.`
        : `Account linked, but the invitation email could not be sent. Ask them to use "Forgot Password".`);
    } catch (err: any) { setError(err.message); }
  };

  // Hard-delete a player with no recorded rounds. The backend refuses (409) if
  // any score exists, in which case the player must be deactivated instead.
  const handleDelete = async (player: Player) => {
    if (!window.confirm(`Permanently delete ${player.first_name} ${player.last_name}? This can't be undone.`)) return;
    try {
      const headers = await authHeader();
      const res = await fetch(`${APIURL}/players/${player.player_id}`, { method: 'DELETE', headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete player.');
      }
      setData((prev) => prev.filter((p) => p.player_id !== player.player_id));
    } catch (err: any) { setError(err.message); }
  };

  if (loading) return <div className="bga-page"><p className="bga-muted">Loading…</p></div>;
  if (error) return <div className="bga-page"><p style={{ color: 'var(--bga-danger)' }}>{error}</p></div>;

  const activePlayers = data.filter((p) => p.league_status !== 'Removed');
  const removedPlayers = data.filter((p) => p.league_status === 'Removed');
  const registered = activePlayers.filter((p) => p.user_id).length;
  const statusFiltered = showRemoved ? data : activePlayers;
  const query = search.trim().toLowerCase();
  const visiblePlayers = query
    ? statusFiltered.filter((p) => {
        const first = (p.first_name || '').toLowerCase();
        const last = (p.last_name || '').toLowerCase();
        return first.includes(query) || last.includes(query)
          || `${first} ${last}`.includes(query) || `${last} ${first}`.includes(query);
      })
    : statusFiltered;

  const sortArrow = (key: keyof Player) =>
    sortConfig?.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="bga-page">
      <div className="bga-page-header">
        <div>
          <h1 className="bga-h1">League Members</h1>
          <p className="bga-sub">
            {registered} of {activePlayers.length} active players have registered accounts.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {removedPlayers.length > 0 && (
            <button className="bga-btn bga-btn-secondary bga-btn-sm" onClick={() => setShowRemoved((v) => !v)}>
              {showRemoved ? 'Hide removed' : `Show ${removedPlayers.length} removed`}
            </button>
          )}
          <button className="bga-btn bga-btn-primary bga-btn-sm" onClick={() => setShowAddModal(true)}>
            + Add Player
          </button>
        </div>
      </div>

      {/* Search by name */}
      <div style={{ marginBottom: 14, position: 'relative', maxWidth: 360 }}>
        <input
          className="bga-input"
          type="text"
          placeholder="Search by first or last name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', paddingRight: 28 }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            aria-label="Clear search"
            title="Clear search"
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1,
            }}
          >×</button>
        )}
      </div>

      <FitTable card>
        <table className="bga-table">
          <thead>
            <tr>
              <th className="col-fit" />
              <th className="bga-sortable" onClick={() => handleSort('player_id')}>ID{sortArrow('player_id')}</th>
              <th className="bga-sortable" onClick={() => handleSort('first_name')}>First Name{sortArrow('first_name')}</th>
              <th className="bga-sortable" onClick={() => handleSort('last_name')}>Last Name{sortArrow('last_name')}</th>
              <th className="bga-sortable" onClick={() => handleSort('league_status')}>Status{sortArrow('league_status')}</th>
              <th>Account</th>
              <th className="bga-sticky-head">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visiblePlayers.map((player) => {
              const aliasList = player.aliases ? player.aliases.split(', ') : [];
              const isRemoved = player.league_status === 'Removed';
              const isEditing = editPlayerId === player.player_id;
              const isExpanded = expandedIds.has(player.player_id) || isEditing;
              return (
                <React.Fragment key={player.player_id}>
                  <tr style={isRemoved ? { opacity: 0.5 } : undefined}>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="bga-expand-btn"
                        onClick={() => toggleExpand(player.player_id)}
                        title={isExpanded ? 'Hide details' : 'Show details'}
                        aria-label={isExpanded ? 'Hide details' : 'Show details'}
                      >{isExpanded ? '▼' : '▶'}</button>
                    </td>
                    <td>{player.player_id}</td>
                    <td>
                      {isEditing
                        ? <input type="text" name="first_name" value={editedPlayer.first_name || ''} onChange={handleInputChange} />
                        : player.first_name}
                    </td>
                    <td>
                      {isEditing
                        ? <input type="text" name="last_name" value={editedPlayer.last_name || ''} onChange={handleInputChange} />
                        : player.last_name}
                    </td>
                    <td>
                      {isRemoved
                        ? <span className="bga-badge bga-badge-red">Removed</span>
                        : isEditing
                          ? <select name="league_status" value={editedPlayer.league_status ?? player.league_status} onChange={handleInputChange}>
                              <option value="Member">Member</option><option value="Guest">Guest</option>
                            </select>
                          : player.league_status}
                    </td>
                    <td>
                      {player.user_id
                        ? <span className="bga-badge bga-badge-green">Registered</span>
                        : <span className="bga-badge bga-badge-amber">Invited</span>}
                    </td>
                    <td className="bga-sticky-col">
                      {isEditing
                        ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button className="bga-btn bga-btn-primary bga-btn-sm" onClick={() => handleSave(player.player_id)}>Save</button>
                            <button className="bga-btn bga-btn-secondary bga-btn-sm" onClick={handleCancel}>Cancel</button>
                          </div>
                        : isRemoved
                          ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button className="bga-btn bga-btn-secondary bga-btn-sm" onClick={() => handleRestore(player)}>Reactivate</button>
                              {!player.has_rounds && (
                                <button className="bga-btn bga-btn-danger bga-btn-sm" onClick={() => handleDelete(player)}>Delete</button>
                              )}
                            </div>
                          : <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button className="bga-btn bga-btn-secondary bga-btn-sm" onClick={() => handleEditClick(player)}>Edit</button>
                              {!player.user_id && (
                                <button className="bga-btn bga-btn-primary bga-btn-sm" onClick={() => handleInvite(player)} title="Account setup incomplete — send the invitation again">Invite</button>
                              )}
                              {player.has_rounds
                                ? <button className="bga-btn bga-btn-danger bga-btn-sm" onClick={() => handleDeactivate(player)}>Deactivate</button>
                                : <button className="bga-btn bga-btn-danger bga-btn-sm" onClick={() => handleDelete(player)}>Delete</button>}
                            </div>}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={isRemoved ? { opacity: 0.5 } : undefined}>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div className="bga-detail-grid">
                          <Detail label="Handedness">
                            {isEditing
                              ? <select name="handedness" value={editedPlayer.handedness ?? player.handedness} onChange={handleInputChange}>
                                  <option value="Right">Right</option><option value="Left">Left</option>
                                </select>
                              : player.handedness}
                          </Detail>
                          <Detail label="Team">
                            {isEditing
                              ? <select name="team" value={editedPlayer.team ?? player.team ?? ''} onChange={handleInputChange}>
                                  <option value="Righties">Righties</option>
                                  <option value="Lefties">Lefties</option>
                                  <option value="None">None</option>
                                </select>
                              : (player.team || 'None')}
                          </Detail>
                          <Detail label="Handicap" hint={HCP_OVERRIDE_HINT}>
                            {isEditing
                              ? <input type="text" name="handicap" value={editedPlayer.handicap ?? player.handicap ?? ''} onChange={handleInputChange} />
                              : player.handicap}
                          </Detail>
                          <Detail label="Tournament Handicap" hint={HCP_OVERRIDE_HINT}>
                            {isEditing
                              ? <input type="text" name="tournament_handicap" value={editedPlayer.tournament_handicap ?? player.tournament_handicap ?? ''} onChange={handleInputChange} />
                              : player.tournament_handicap}
                          </Detail>
                          <Detail label="Username">
                            <span style={{ color: player.username ? '#222' : '#bbb' }}>{player.username || '—'}</span>
                          </Detail>
                          <Detail label="BGA Aliases">
                            {aliasList.length > 0
                              ? aliasList.map((a) => <div key={a}><code>{a}</code></div>)
                              : <span style={{ color: '#bbb' }}>—</span>}
                          </Detail>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </FitTable>

      {visiblePlayers.length === 0 && (
        <p className="bga-muted" style={{ textAlign: 'center', padding: '20px 0' }}>
          {query ? `No players match “${search}”.` : 'No players yet.'}
        </p>
      )}

      {showAddModal && (
        <div
          onClick={() => !adding && setShowAddModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '8vh 16px', zIndex: 1000,
          }}
        >
          <div
            className="bga-card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 460, margin: 0 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 className="bga-card-title" style={{ margin: 0 }}>Add a player</h2>
              <button
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, lineHeight: 1, color: '#94a3b8' }}
              >×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="bga-field">
                <label className="bga-label">First name</label>
                <input className="bga-input" type="text" autoFocus value={newPlayer.first_name}
                  onChange={(e) => setNewPlayer((p) => ({ ...p, first_name: e.target.value }))} />
              </div>
              <div className="bga-field">
                <label className="bga-label">Last name</label>
                <input className="bga-input" type="text" value={newPlayer.last_name}
                  onChange={(e) => setNewPlayer((p) => ({ ...p, last_name: e.target.value }))} />
              </div>
              <div className="bga-field">
                <label className="bga-label">Email</label>
                <input className="bga-input" type="email" placeholder="player@email.com" value={newPlayer.email}
                  onChange={(e) => setNewPlayer((p) => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <p className="bga-muted" style={{ fontSize: 12.5, margin: '12px 0 0' }}>
              An invitation email is sent automatically so the player can set up their account.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="bga-btn bga-btn-secondary" onClick={() => setShowAddModal(false)} disabled={adding}>Cancel</button>
              <button className="bga-btn bga-btn-primary" onClick={handleAddPlayer} disabled={adding}>
                {adding ? 'Creating…' : '+ Add Player'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;
